import { normalizeIranNationalCode, normalizeSearchText } from '@clinic/money-date';
import { z } from 'zod';
import {
  DateOnlySchema,
  IsoDateTimeSchema,
  PAGE_LIMIT_DEFAULT,
  PAGE_LIMIT_MAX,
  PhoneInputSchema,
  UuidSchema,
  optionalText,
  pageSchema,
} from './common.js';

/** Underlying conditions from the legacy app's `histList` (docs/legacy/reference-data.md §3). */
export const MEDICAL_CONDITIONS = [
  'herpes',
  'diabetes',
  'hypertension',
  'heart_disease',
  'thyroid',
  'anemia',
  'autoimmune',
  'epilepsy',
  'hepatitis',
  'bleeding_disorder',
  'pregnancy_or_breastfeeding',
] as const;
export type MedicalCondition = (typeof MEDICAL_CONDITIONS)[number];

/** Prior cosmetic injections a patient may have had (employer request 2026-10-05 #4). */
export const PRIOR_INJECTION_TYPES = [
  'botox',
  'filler',
  'mesotherapy',
  'prp',
  'thread',
  'skin_booster',
  'fat_dissolver',
  'biostimulator',
  'endolift',
  'hifu',
  'rf',
  'pdrn_pn',
  'exosome',
] as const;
export type PriorInjectionType = (typeof PRIOR_INJECTION_TYPES)[number];

/** How the patient found the clinic (master-spec §Y, idea 2.1). */
export const REFERRAL_SOURCES = [
  'instagram',
  'google',
  'website',
  'referral_patient',
  'referral_doctor',
  'walk_in',
  'other',
] as const;
export type ReferralSource = (typeof REFERRAL_SOURCES)[number];

export const GENDERS = ['female', 'male'] as const;
export type Gender = (typeof GENDERS)[number];

/** The two values of the legacy app (docs/legacy/reference-data.md §3). */
export const MARITAL_STATUSES = ['single', 'married'] as const;
export type MaritalStatus = (typeof MARITAL_STATUSES)[number];

export const PATIENT_ADDRESS_MAX = 300;
export const PATIENT_FILE_NUMBER_MAX = 30;

export const PATIENT_STATUSES = ['active', 'archived'] as const;

export const PatientSchema = z.object({
  id: UuidSchema,
  firstName: z.string(),
  lastName: z.string().nullable(),
  phone: z.string().nullable(),
  nationalCode: z.string().nullable(),
  /** The clinic's own file number, normalized (BR-73). */
  fileNumber: z.string().nullable(),
  /** Required fields the user marked «ندارد» (BR-50). */
  exemptFields: z.array(z.enum(['lastName', 'phone', 'nationalCode'])),
  gender: z.enum(GENDERS).nullable(),
  birthDate: z.string().nullable(),
  maritalStatus: z.enum(MARITAL_STATUSES).nullable(),
  address: z.string().nullable(),
  referralSource: z.enum(REFERRAL_SOURCES).nullable(),
  referredByPatientId: UuidSchema.nullable(),
  status: z.enum(PATIENT_STATUSES),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Patient = z.infer<typeof PatientSchema>;

/** Iranian national code: any digits and separators in, ten Latin digits out (check digit verified). */
export const NationalCodeInputSchema = z
  .string()
  .trim()
  .max(24)
  .transform((value, ctx) => {
    if (value === '') return null;
    const normalized = normalizeIranNationalCode(value);
    if (!normalized) {
      ctx.addIssue({ code: 'custom', message: 'کد ملی معتبر نیست.' });
      return z.NEVER;
    }
    return normalized;
  });

/**
 * The clinic's own file number (BR-73), for clinics that already keep numbered files. Normalized
 * like search text (Latin digits, one letter form, ASCII lower case, single spaces), so «۱۲۳» and
 * «123» are one number; empty is none.
 */
export const FileNumberInputSchema = z
  .string()
  .trim()
  .max(60)
  .transform((value, ctx) => {
    const normalized = normalizeSearchText(value);
    if (normalized === '') return null;
    if (normalized.length > PATIENT_FILE_NUMBER_MAX) {
      ctx.addIssue({ code: 'custom', message: 'شماره‌ی پرونده حداکثر ۳۰ نویسه است.' });
      return z.NEVER;
    }
    return normalized;
  });

/**
 * The required fields a user may mark «ندارد» for a special case (owner decision, 2026-10-02):
 * e.g. a foreign national has no Iranian national code. The first name is always required: a
 * file without a name cannot be found.
 */
export const EXEMPTIBLE_PATIENT_FIELDS = ['lastName', 'phone', 'nationalCode'] as const;
export type ExemptiblePatientField = (typeof EXEMPTIBLE_PATIENT_FIELDS)[number];

const REQUIRED_MESSAGE: Readonly<Record<ExemptiblePatientField, string>> = {
  lastName: 'نام خانوادگی را وارد کنید.',
  phone: 'شماره‌ی موبایل را وارد کنید.',
  nationalCode: 'کد ملی را وارد کنید.',
};

/**
 * Basic profile only. Medical data has its own endpoint and permission (Master Prompt §16).
 * First and last name, mobile and national code are required (pilot feedback #8, BR-50), unless
 * the user marked one of the last three «ندارد» (`exemptFields`); an exempt field is empty. The
 * database keeps the fields nullable for imported and older files, which must be completed (or
 * marked) on their next edit.
 */
export const PatientInputSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(1, { message: 'نام را وارد کنید.' })
      .max(60, { message: 'نام حداکثر ۶۰ نویسه است.' }),
    lastName: optionalText(60),
    phone: z
      .preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), PhoneInputSchema.nullish())
      .transform((v) => v ?? null),
    nationalCode: NationalCodeInputSchema.nullish().transform((v) => v ?? null),
    fileNumber: FileNumberInputSchema.nullish().transform((v) => v ?? null),
    exemptFields: z
      .array(z.enum(EXEMPTIBLE_PATIENT_FIELDS))
      .max(10)
      .default([])
      .transform((v) => EXEMPTIBLE_PATIENT_FIELDS.filter((f) => v.includes(f))),
    gender: z
      .enum(GENDERS)
      .nullish()
      .transform((v) => v ?? null),
    birthDate: DateOnlySchema.nullish()
      .transform((v) => v ?? null)
      .refine((v) => v === null || (v >= '1900-01-01' && v <= new Date().toISOString().slice(0, 10)), {
        message: 'تاریخ تولد معتبر نیست.',
      }),
    maritalStatus: z
      .enum(MARITAL_STATUSES)
      .nullish()
      .transform((v) => v ?? null),
    address: optionalText(PATIENT_ADDRESS_MAX),
    referralSource: z
      .enum(REFERRAL_SOURCES)
      .nullish()
      .transform((v) => v ?? null),
    referredByPatientId: UuidSchema.nullish().transform((v) => v ?? null),
  })
  .superRefine(
    (p, ctx) => {
      for (const field of EXEMPTIBLE_PATIENT_FIELDS) {
        const exempt = p.exemptFields.includes(field);
        if (!exempt && p[field] === null) {
          ctx.addIssue({ code: 'custom', path: [field], message: REQUIRED_MESSAGE[field] });
        }
        // A field whose own parsing failed is neither null nor a string: it already has its message.
        if (exempt && typeof p[field] === 'string') {
          ctx.addIssue({ code: 'custom', path: [field], message: 'این خانه «ندارد» زده شده؛ خالی بماند.' });
        }
      }
    },
    // Runs even when another field is wrong, so the form shows every missing field at once;
    // a field that already has its own error (e.g. an invalid code) gets no second message.
    {
      // Not for a body that is no object at all (a JSON array or a string): it has its own error.
      when: (payload) =>
        !payload.issues.some((i) => i.path?.[0] === 'exemptFields') &&
        typeof payload.value === 'object' &&
        payload.value !== null &&
        !Array.isArray(payload.value) &&
        Array.isArray((payload.value as { exemptFields?: unknown }).exemptFields),
    },
  );
export type PatientInput = z.input<typeof PatientInputSchema>;

export const PatientListQuerySchema = z.object({
  q: z.string().trim().max(60).optional(),
  status: z.enum(PATIENT_STATUSES).default('active'),
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(PAGE_LIMIT_DEFAULT),
});
export type PatientListQuery = z.infer<typeof PatientListQuerySchema>;

export const PatientListResponseSchema = pageSchema(PatientSchema);
export type PatientListResponse = z.infer<typeof PatientListResponseSchema>;

export const MedicalProfileSchema = z.object({
  patientId: UuidSchema,
  conditions: z.array(z.enum(MEDICAL_CONDITIONS)),
  otherConditions: z.string().nullable(),
  allergies: z.string().nullable(),
  medications: z.string().nullable(),
  notes: z.string().nullable(),
  /** null = not asked yet, false = none, true = had some (then the list and «other»). */
  priorInjections: z.boolean().nullable(),
  priorInjectionTypes: z.array(z.enum(PRIOR_INJECTION_TYPES)),
  priorInjectionsOther: z.string().nullable(),
  /** null until the first save. */
  updatedAt: IsoDateTimeSchema.nullable(),
});
export type MedicalProfile = z.infer<typeof MedicalProfileSchema>;

export const MedicalProfileInputSchema = z.object({
  conditions: z
    .array(z.enum(MEDICAL_CONDITIONS))
    .max(MEDICAL_CONDITIONS.length)
    .refine((v) => new Set(v).size === v.length, { message: 'مورد تکراری است.' })
    .default([]),
  otherConditions: optionalText(500),
  allergies: optionalText(1000),
  medications: optionalText(1000),
  notes: optionalText(2000),
  priorInjections: z.boolean().nullable().default(null),
  priorInjectionTypes: z
    .array(z.enum(PRIOR_INJECTION_TYPES))
    .max(PRIOR_INJECTION_TYPES.length)
    .refine((v) => new Set(v).size === v.length, { message: 'مورد تکراری است.' })
    .default([]),
  priorInjectionsOther: optionalText(500),
}).refine((v) => v.priorInjections === true || (v.priorInjectionTypes.length === 0 && v.priorInjectionsOther === null), {
  message: 'نوع تزریق فقط وقتی «دارد» انتخاب شده است.',
  path: ['priorInjectionTypes'],
});
export type MedicalProfileInput = z.input<typeof MedicalProfileInputSchema>;

export const ClinicalNoteSchema = z.object({
  id: UuidSchema,
  patientId: UuidSchema,
  visitId: UuidSchema.nullable(),
  authorMembershipId: UuidSchema,
  authorName: z.string().nullable(),
  body: z.string(),
  amendsId: UuidSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type ClinicalNote = z.infer<typeof ClinicalNoteSchema>;

export const ClinicalNoteInputSchema = z.object({
  body: z
    .string()
    .trim()
    .min(1, { message: 'متن یادداشت را بنویسید.' })
    .max(10_000, { message: 'یادداشت حداکثر ۱۰٬۰۰۰ نویسه است.' }),
  visitId: UuidSchema.nullish().transform((v) => v ?? null),
  amendsId: UuidSchema.nullish().transform((v) => v ?? null),
});
export type ClinicalNoteInput = z.input<typeof ClinicalNoteInputSchema>;

export const ClinicalNoteListResponseSchema = z.object({ items: z.array(ClinicalNoteSchema) });
export type ClinicalNoteListResponse = z.infer<typeof ClinicalNoteListResponseSchema>;
